import { CartItem } from './types'
import { Order, OrderItem, OrderItemOption } from './types'
import { formatCurrency, aggregateQuantityOptions } from './utils'

const CLOUDINARY_CLOUD_NAME =
  import.meta?.env?.VITE_CLOUDINARY_CLOUD_NAME || 'djp4z0hdx'
const CLOUDINARY_UPLOAD_PRESET = 'tickets_restaurante'

export interface TicketData {
  restaurantName: string
  currencySymbol: string
  orderNumber: string
  customerName: string
  customerPhone: string
  deliveryType: string
  deliveryAddress?: string | null
  mapsUrl?: string | null
  items: CartItem[]
  subtotal: number
  deliveryFee: number
  total: number
  notes?: string | null
  createdAt: string
  deliveryZone?: string | null
  deliverySubzone?: string | null
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word
    if (ctx.measureText(testLine).width > maxWidth && currentLine) {
      lines.push(currentLine)
      currentLine = word
    } else {
      currentLine = testLine
    }
  }
  if (currentLine) lines.push(currentLine)
  return lines
}

export function generateTicketImage(data: TicketData): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      const W = 400
      const PAD = 28
      const CONTENT_W = W - PAD * 2
      const cs = data.currencySymbol
      const fmt = (n: number) => n.toLocaleString()

      // Pre-calculate height
      const canvas = document.createElement('canvas')
      canvas.width = W
      canvas.height = 2000 // temporary tall canvas
      const ctx = canvas.getContext('2d')!
      ctx.font = '13px Inter, system-ui, sans-serif'

      let estimatedHeight = 320 // base height for header, footer, totals
      for (const item of data.items) {
        estimatedHeight += 22
        estimatedHeight += item.selected_options.length * 18
        if (item.notes) estimatedHeight += 18
      }
      if (data.deliveryAddress) {
        ctx.font = '12px Inter, system-ui, sans-serif'
        estimatedHeight +=
          20 + wrapText(ctx, data.deliveryAddress, CONTENT_W).length * 16
      }
      if (data.mapsUrl) estimatedHeight += 18
      if (data.notes) {
        ctx.font = '12px Inter, system-ui, sans-serif'
        estimatedHeight += 20 + wrapText(ctx, data.notes, CONTENT_W).length * 16
      }

      // Create final canvas
      const H = Math.max(estimatedHeight, 400)
      canvas.height = H
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, W, H)

      let y = PAD

      // Header
      ctx.fillStyle = '#111827'
      ctx.font = 'bold 18px Inter, system-ui, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(data.restaurantName, W / 2, y + 18)
      y += 32

      ctx.font = '11px Inter, system-ui, sans-serif'
      ctx.fillStyle = '#6B7280'
      ctx.fillText('Pedido vía NOIZA', W / 2, y)
      y += 20

      // Dashed line
      ctx.setLineDash([4, 3])
      ctx.strokeStyle = '#D1D5DB'
      ctx.beginPath()
      ctx.moveTo(PAD, y)
      ctx.lineTo(W - PAD, y)
      ctx.stroke()
      ctx.setLineDash([])
      y += 16

      // Order info
      ctx.textAlign = 'left'
      ctx.font = 'bold 14px Inter, system-ui, sans-serif'
      ctx.fillStyle = '#111827'
      ctx.fillText(`Orden #${data.orderNumber}`, PAD, y)
      y += 18

      ctx.font = '12px Inter, system-ui, sans-serif'
      ctx.fillStyle = '#6B7280'
      const dateStr = new Date(data.createdAt).toLocaleString('es-CR', {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
      ctx.fillText(dateStr, PAD, y)
      y += 22

      ctx.fillStyle = '#374151'
      ctx.fillText(`👤 ${data.customerName}`, PAD, y)
      y += 17
      ctx.fillText(`📞 ${data.customerPhone}`, PAD, y)
      y += 17

      const deliveryLabel =
        data.deliveryType === 'pickup'
          ? 'Para llevar'
          : data.deliveryType === 'delivery'
            ? 'Delivery'
            : 'Comer aquí'
      ctx.fillText(`📦 ${deliveryLabel}`, PAD, y)
      y += 20

      // Dashed line
      ctx.setLineDash([4, 3])
      ctx.strokeStyle = '#D1D5DB'
      ctx.beginPath()
      ctx.moveTo(PAD, y)
      ctx.lineTo(W - PAD, y)
      ctx.stroke()
      ctx.setLineDash([])
      y += 16

      // Items
      for (const item of data.items) {
        const optTotal = item.selected_options.reduce(
          (s, o) => s + o.price_delta,
          0,
        )
        const lineTotal = (item.unit_price_snapshot + optTotal) * item.quantity

        ctx.font = '13px Inter, system-ui, sans-serif'
        ctx.fillStyle = '#111827'
        ctx.textAlign = 'left'
        ctx.fillText(`${item.quantity}x ${item.item_name_snapshot}`, PAD, y)
        ctx.textAlign = 'right'
        ctx.fillText(`${cs}${fmt(lineTotal)}`, W - PAD, y)
        ctx.textAlign = 'left'
        y += 20

        for (const opt of aggregateQuantityOptions(item.selected_options)) {
          ctx.font = '11px Inter, system-ui, sans-serif'
          ctx.fillStyle = '#9CA3AF'
          const delta =
            opt.price_delta > 0 ? ` +${cs}${fmt(opt.price_delta)}` : ''
          const qtySuffix = opt.qty > 1 ? ` x${opt.qty}` : ''
          ctx.fillText(`   • ${opt.option_name}${qtySuffix}${delta}`, PAD, y)
          y += 16
        }

        if (item.notes) {
          ctx.font = 'italic 11px Inter, system-ui, sans-serif'
          ctx.fillStyle = '#D97706'
          ctx.fillText(`   📝 ${item.notes}`, PAD, y)
          y += 16
        }
      }

      y += 4

      // Dashed line
      ctx.setLineDash([4, 3])
      ctx.strokeStyle = '#D1D5DB'
      ctx.beginPath()
      ctx.moveTo(PAD, y)
      ctx.lineTo(W - PAD, y)
      ctx.stroke()
      ctx.setLineDash([])
      y += 16

      // Totals
      ctx.font = '13px Inter, system-ui, sans-serif'
      ctx.fillStyle = '#374151'
      ctx.textAlign = 'left'
      ctx.fillText('Subtotal', PAD, y)
      ctx.textAlign = 'right'
      ctx.fillText(`${cs}${fmt(data.subtotal)}`, W - PAD, y)
      y += 20

      if (data.deliveryFee > 0) {
        ctx.textAlign = 'left'
        ctx.fillText('Envío', PAD, y)
        ctx.textAlign = 'right'
        ctx.fillText(`${cs}${fmt(data.deliveryFee)}`, W - PAD, y)
        y += 20
      }

      ctx.font = 'bold 15px Inter, system-ui, sans-serif'
      ctx.fillStyle = '#111827'
      ctx.textAlign = 'left'
      ctx.fillText('Total', PAD, y)
      ctx.textAlign = 'right'
      ctx.fillText(`${cs}${fmt(data.total)}`, W - PAD, y)
      y += 24

      // Delivery address
      if (data.deliveryAddress) {
        ctx.font = 'bold 12px Inter, system-ui, sans-serif'
        ctx.fillStyle = '#374151'
        ctx.textAlign = 'left'
        ctx.fillText('📍 Dirección:', PAD, y)
        y += 16
        ctx.font = '12px Inter, system-ui, sans-serif'
        ctx.fillStyle = '#6B7280'

        if (data.deliveryZone && data.deliverySubzone) {
          ctx.fillText(`Zona: ${data.deliveryZone}`, PAD, y)
          y += 16
          ctx.fillText(`Subzona: ${data.deliverySubzone}`, PAD, y)
          y += 16
        }

        const addrLines = wrapText(ctx, data.deliveryAddress, CONTENT_W)
        for (const line of addrLines) {
          ctx.fillText(line, PAD, y)
          y += 16
        }
        y += 4
      }

      if (data.mapsUrl) {
        ctx.font = '11px Inter, system-ui, sans-serif'
        ctx.fillStyle = '#2563EB'
        ctx.textAlign = 'left'
        ctx.fillText(`🗺️ ${data.mapsUrl}`, PAD, y)
        y += 18
      }

      // Notes
      if (data.notes) {
        ctx.font = 'bold 12px Inter, system-ui, sans-serif'
        ctx.fillStyle = '#374151'
        ctx.textAlign = 'left'
        ctx.fillText('📝 Notas:', PAD, y)
        y += 16
        ctx.font = '12px Inter, system-ui, sans-serif'
        ctx.fillStyle = '#6B7280'
        const noteLines = wrapText(ctx, data.notes, CONTENT_W)
        for (const line of noteLines) {
          ctx.fillText(line, PAD, y)
          y += 16
        }
      }

      // Trim canvas to actual content height
      const finalH = y + PAD
      const finalCanvas = document.createElement('canvas')
      finalCanvas.width = W
      finalCanvas.height = finalH
      const fCtx = finalCanvas.getContext('2d')!
      fCtx.fillStyle = '#FFFFFF'
      fCtx.fillRect(0, 0, W, finalH)
      fCtx.drawImage(canvas, 0, 0)

      finalCanvas.toBlob(
        (blob) => {
          if (blob) resolve(blob)
          else reject(new Error('Failed to generate ticket blob'))
        },
        'image/png',
        1,
      )
    } catch (err) {
      reject(err)
    }
  })
}

export async function uploadTicketToCloudinary(
  blob: Blob,
  orderNumber: string,
): Promise<string | null> {
  if (!CLOUDINARY_CLOUD_NAME) {
    console.warn(
      '[NOIZA] Missing VITE_CLOUDINARY_CLOUD_NAME. Ticket upload skipped.',
    )
    return null
  }

  try {
    const formData = new FormData()
    formData.append('file', blob, `ticket-${orderNumber}.png`)
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET)
    formData.append('public_id', `noiza/tickets/${orderNumber}`)

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      { method: 'POST', body: formData },
    )

    if (!response.ok) {
      const errText = await response.text()
      console.error(
        '[NOIZA] Cloudinary upload failed:',
        response.status,
        errText,
      )
      return null
    }

    const result = await response.json()
    return result.secure_url || null
  } catch (err) {
    console.error('[NOIZA] Cloudinary upload error:', err)
    return null
  }
}

export async function uploadPaymentProofToCloudinary(
  file: Blob | File,
  restaurantSlug: string,
  orderTag: string,
): Promise<string | null> {
  if (!CLOUDINARY_CLOUD_NAME) {
    console.warn(
      '[NOIZA] Missing VITE_CLOUDINARY_CLOUD_NAME. Payment proof upload skipped.',
    )
    return null
  }

  try {
    const safeSlug =
      (restaurantSlug || 'restaurant')
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || 'restaurant'
    const safeTag =
      (orderTag || `proof-${Date.now()}`)
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || `proof-${Date.now()}`

    const fileName =
      file instanceof File ? file.name : `payment-proof-${Date.now()}.png`

    const formData = new FormData()
    formData.append('file', file, fileName)
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET)
    formData.append('public_id', `noiza/payment-proofs/${safeSlug}/${safeTag}`)

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      { method: 'POST', body: formData },
    )

    if (!response.ok) {
      const errText = await response.text()
      console.error(
        '[NOIZA] Cloudinary payment proof upload failed:',
        response.status,
        errText,
      )
      return null
    }

    const result = await response.json()
    return result.secure_url || null
  } catch (err) {
    console.error('[NOIZA] Cloudinary payment proof upload error:', err)
    return null
  }
}
